import axios, { type AxiosProgressEvent } from 'axios';

// Get auth token from localStorage
const getAuthToken = () => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create storage API instance
const storageApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/storage/api' : '/storage/api',
});

// Add request interceptor to include auth token
storageApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for auth errors
storageApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// Storage Service Methods
export const storageService = {
    /**
     * Get list of all buckets
     */
    getBuckets: async (): Promise<string[]> => {
        const response = await storageApi.get<string[]>('/buckets');
        return response.data;
    },

    /**
     * Get bucket info (list all objects in bucket)
     */
    getBucketInfo: async (bucketName: string) => {
        const response = await storageApi.get('/info', {
            params: { bucketName }
        });
        return response.data;
    },

    /**
     * Get folder info (list objects in a specific folder)
     */
    getFolderInfo: async (bucketName: string, folderName: string) => {
        const response = await storageApi.get('/folder/info', {
            params: { bucketName, folderName }
        });
        return response.data;
    },

    /**
     * Upload a file to a bucket/folder
     */
    uploadFile: async (
        file: File,
        bucketName: string,
        folderName: string,
        tags: string = '',
        onProgress?: (event: AxiosProgressEvent) => void
    ) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bucketName', bucketName);
        formData.append('folderName', folderName);
        formData.append('tags', tags);

        const response = await storageApi.post('/upload', formData, {
            onUploadProgress: onProgress
        });
        return response.data;
    },

    /**
     * Download a file
     */
    downloadFile: async (bucketName: string, objectName: string) => {
        const response = await storageApi.get('/download', {
            params: { bucketName, objectName },
            responseType: 'blob'
        });
        return {
            data: response.data,
            headers: response.headers
        };
    },

    /**
     * Search files by name
     */
    searchFiles: async (name: string) => {
        const response = await storageApi.get('/search', {
            params: { name }
        });
        return response.data;
    },

    /**
     * Delete files from a bucket
     */
    deleteFiles: async (bucketName: string, objectList: string[]) => {
        const response = await storageApi.delete('/objects', {
            data: { bucketName, objectList }
        });
        return response.data;
    },

    /**
     * Create a new bucket
     */
    createBucket: async (bucketName: string) => {
        const response = await storageApi.post('/buckets/create', { bucketName }, {
            params: { bucketName }
        });
        return response.data;
    },

    /**
     * Delete a bucket
     */
    deleteBucket: async (bucketName: string) => {
        const response = await storageApi.delete(`/bucket/${bucketName}`);
        return response.data;
    },

    /**
     * Update tags for an object
     */
    updateTags: async (bucketName: string, objectName: string, tags: Record<string, string>) => {
        const response = await storageApi.put(`/buckets/${bucketName}/objects/${objectName}/tags`, tags);
        return response.data;
    }
};

export default storageService;
